import React, { useState, useCallback } from 'react';
import {
  Box,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { SelectInputSmall } from '../select-input-small';
import { TextInputSmall } from '../text-input-small';
import styles from './select-or-create.module.scss';

interface SelectOption {
  id: number;
  name: string;
}

interface SelectOrCreateProps {
  label: string;
  value?: SelectOption | null;
  newValue?: string;
  options: SelectOption[];
  onSelectChange: (selected: SelectOption | null) => void;
  onNewValueChange: (newValue: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  mode?: 'select' | 'create'; // Режим по умолчанию
  onModeChange?: (mode: 'select' | 'create') => void;
}

export const SelectOrCreate: React.FC<SelectOrCreateProps> = ({
  label,
  value,
  newValue = '',
  options,
  onSelectChange,
  onNewValueChange,
  placeholder = 'Выберите или создайте',
  disabled = false,
  error = false,
  mode = 'select',
  onModeChange,
}) => {
  const [currentMode, setCurrentMode] = useState<'select' | 'create'>(mode);

  const handleModeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newMode = event.target.value as 'select' | 'create';
      setCurrentMode(newMode);
      if (onModeChange) {
        onModeChange(newMode);
      }

      // Сбрасываем значения при смене режима
      if (newMode === 'select') {
        onNewValueChange('');
      } else {
        onSelectChange(null);
      }
    },
    [onModeChange, onNewValueChange, onSelectChange],
  );

  const handleSelectChange = useCallback(
    (event: any) => {
      const selectedId = event.target.value as number;
      const selectedOption = options.find((option) => option.id === selectedId) || null;
      onSelectChange(selectedOption);
    },
    [options, onSelectChange],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onNewValueChange(event.target.value);
    },
    [onNewValueChange],
  );

  return (
    <Box className={styles.container}>
      <FormControl component='fieldset' disabled={disabled} className={styles.radioGroup}>
        <FormLabel component='legend' className={styles.label}>
          {label}
        </FormLabel>
        <RadioGroup
          row
          value={currentMode}
          onChange={handleModeChange}
          className={styles.radioButtons}
        >
          <FormControlLabel
            value='select'
            control={<Radio size='small' />}
            label='Выбрать существующий'
            className={styles.radioButton}
          />
          <FormControlLabel
            value='create'
            control={<Radio size='small' />}
            label='Создать новый'
            className={styles.radioButton}
          />
        </RadioGroup>
      </FormControl>

      <Box className={styles.inputContainer}>
        {currentMode === 'select' ? (
          <SelectInputSmall
            value={value?.id || ''}
            onChange={handleSelectChange}
            placeholder={placeholder}
            disabled={disabled || options.length === 0}
            error={error}
            fullWidth
          >
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </SelectInputSmall>
        ) : (
          <TextInputSmall
            value={newValue}
            onChange={handleInputChange}
            placeholder={`Введите название ${label.toLowerCase()}`}
            disabled={disabled}
            error={error}
            fullWidth
          />
        )}
      </Box>
    </Box>
  );
};
